import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { HoraireHebdoDto } from "../../../dto/GenApi.iNotPaie.Dto/HoraireHebdoDto";
import { HoraireJourneeDto } from "../../../dto/GenApi.iNotPaie.Dto/HoraireJourneeDto";
import { NotificationServiceInstance } from "../../../shared/services/notification.service";
import { ErreursValidationJournee, JourHoraireVue } from "../../../shared/interfaces/horaires.interface";
import {
	CHAMPS_HORAIRE,
	formatDureeAffichage as formatDureeHhMm,
	formatHeureAffichage,
	infererJoursActifsDepuisHoraire,
	JOURS_ACTIFS_PAR_DEFAUT,
	journeesHoraireHebdoOrdonnees,
	libelleJourIndex,
	normaliserCreneauxVidesJournee,
	normaliserHoraireHebdoRecu,
	parseHeureInput,
	premiereJourneeActiveAvecValeur,
	reinitialiserHoraireHebdo,
	totalJourneeHhMm,
	totalSemaineHhMm,
	totalSemaineModeIdentiqueHhMm,
	validerJourneeHoraire,
	viderJourneeHoraire
} from "../../../Helper/horaireHebdoHelper";

@Component
export default class ModalHoraireHebdo extends Vue {
	@Prop({ default: undefined })
	public horaireHebdoInitial: HoraireHebdoDto | undefined;

	@Prop({ default: undefined, required: false })
	public heuresParSemaine: string | undefined;

	public horaireHebdo: HoraireHebdoDto = {
		Id: 0,
		HoraireJourneeLundi: { Id: 0 },
		HoraireJourneeMardi: { Id: 0 },
		HoraireJourneeMercredi: { Id: 0 },
		HoraireJourneeJeudi: { Id: 0 },
		HoraireJourneeVendredi: { Id: 0 },
		HoraireJourneeSamedi: { Id: 0 }
	};

	public joursActifs: boolean[] = [...JOURS_ACTIFS_PAR_DEFAUT];
	public horairesIdentiques = false;
	public journeeModeIdentique: HoraireJourneeDto = { Id: 0 };
	public champsEnErreur: string[] = [];
	public visible = false;

	private snapshotHoraireAvantIdentique: HoraireHebdoDto | null = null;
	private snapshotJoursActifsAvantIdentique: boolean[] | null = null;

	public mounted(): void {
		this.ouvrir();
	}

	/**
	 * Retourne les journées ordonnées (lundi → samedi).
	 * @returns Les journées ordonnées.
	 */
	private get journees(): HoraireJourneeDto[] {
		return journeesHoraireHebdoOrdonnees(this.horaireHebdo);
	}

	/**
	 * Retourne les totaux journaliers HH:mm, indexés comme les journées.
	 * @returns Un tableau de durées "HH:mm" (une par jour).
	 */
	private get totauxJournaliers(): string[] {
		return this.journees.map((journee, index) => totalJourneeHhMm(journee, this.joursActifs[index]));
	}

	/**
	 * Retourne les lignes du tableau mode personnalisé (réactif aux saisies et totaux).
	 * @returns Les lignes formatées pour le template.
	 */
	public get joursHoraireVue(): JourHoraireVue[] {
		return this.journees.map((journee, index) => {
			const totalJournee = this.totauxJournaliers[index];

			return {
				index,
				libelle: libelleJourIndex(index),
				journee,
				actif: this.joursActifs[index],
				totalJournee,
				afficherAppliquerATous:
					this.joursActifs[index] && totalJournee !== "" && !this.horairesIdentiques
			};
		});
	}

	/**
	 * Retourne le total de la ligne mode identique.
	 * @returns La durée "HH:mm" ou `""`.
	 */
	public get totalModeIdentique(): string {
		return totalJourneeHhMm(this.journeeModeIdentique, true);
	}

	/**
	 * Retourne le total hebdomadaire (somme des jours actifs).
	 * @returns La durée "HH:mm".
	 */
	public get totalSemaine(): string {
		if (this.horairesIdentiques) {
			return totalSemaineModeIdentiqueHhMm(this.journeeModeIdentique, this.joursActifs);
		}

		return totalSemaineHhMm(this.totauxJournaliers, this.joursActifs);
	}

	/**
	 * Bascule la case « horaires identiques ».
	 * @param checked - `true` pour activer le mode identique.
	 */
	public toggleHorairesIdentiques(checked: boolean): void {
		if (checked) {
			this.activerModeIdentique();
		} else {
			this.desactiverModeIdentique();
		}
	}

	/**
	 * Charge l'horaire initial et affiche la modale.
	 */
	public ouvrir(): void {
		this.champsEnErreur = [];
		this.horairesIdentiques = false;
		this.snapshotHoraireAvantIdentique = null;
		this.snapshotJoursActifsAvantIdentique = null;
		this.horaireHebdo = this.horaireHebdoInitial ? normaliserHoraireHebdoRecu(this.horaireHebdoInitial) : this.horaireHebdo;
		this.joursActifs = infererJoursActifsDepuisHoraire(this.horaireHebdo);
		this.journeeModeIdentique = { ...this.journees[0] };
		this.visible = true;
	}

	/**
	 * Valide, normalise et émet l'horaire mis à jour.
	 * En cas d'erreur, affiche les messages et met en surbrillance les champs fautifs.
	 */
	public valider(): void {
		if (this.horairesIdentiques) {
			this.synchroniserJoursActifsDepuisModeIdentique();
		}

		const erreurs = this.collecterErreursValidation();

		if (erreurs.messages.length > 0) {
			this.champsEnErreur = erreurs.champsErreur;
			NotificationServiceInstance.SendFailedList(erreurs.messages);

			return;
		}

		this.champsEnErreur = [];

		// Préparer la copie de sauvegarde avec normalisation des jours inactifs
		const horairePourSauvegarde: HoraireHebdoDto = JSON.parse(JSON.stringify(this.horaireHebdo));
		journeesHoraireHebdoOrdonnees(horairePourSauvegarde).forEach((journee, index) => {
			if (!this.joursActifs[index]) {
				viderJourneeHoraire(journee);
			} else {
				normaliserCreneauxVidesJournee(journee);
			}
		});

		this.$emit("horaireHebdoUpdated", horairePourSauvegarde);
		this.visible = false;
	}

	/**
	 * Active ou désactive un jour de la semaine.
	 * @param indexJour - L'index du jour (0 = lundi … 5 = samedi).
	 * @param actif - `true` pour cocher, `false` pour décocher.
	 */
	public onJourActifChanged(indexJour: number, actif: boolean): void {
		this.$set(this.joursActifs, indexJour, actif);
		if (!actif) {
			viderJourneeHoraire(this.journees[indexJour]);
		}
	}

	/**
	 * Saisie horaire en mode identique.
	 * @param field - Le champ modifié (ex. "MatinDebut").
	 * @param event - L'événement natif du champ time.
	 */
	public onTimeModeIdentiqueChanged(field: keyof HoraireJourneeDto, event: Event): void {
		this.appliquerSaisieHeure(this.journeeModeIdentique, field, event);
		this.synchroniserJoursActifsDepuisModeIdentique();
	}

	/**
	 * Saisie horaire en mode personnalisé.
	 * @param journee - La journée concernée.
	 * @param field - Le champ modifié (ex. "ApremFin").
	 * @param event - L'événement natif du champ time.
	 */
	public onTimeChanged(journee: HoraireJourneeDto, field: keyof HoraireJourneeDto, event: Event): void {
		this.appliquerSaisieHeure(journee, field, event);
		if (this.horairesIdentiques) {
			this.synchroniserJoursActifsDepuisModeIdentique();
		}
	}

	/**
	 * Copie les horaires d'une ligne vers tous les autres jours actifs.
	 * @param indexJourSource - L'index de la journée source.
	 */
	public appliquerATous(indexJourSource: number): void {
		const journeeSource = this.journees[indexJourSource];
		this.journees.forEach((journee, index) => {
			if (!this.joursActifs[index] || index === indexJourSource) {
				return;
			}
			this.copierJourneeReactive(journeeSource, journee);
		});
	}

	/**
	 * Remet l'état initial (lun–ven cochés, créneaux vides).
	 */
	public reinitialiser(): void {
		this.horaireHebdo = reinitialiserHoraireHebdo(this.horaireHebdo);
		this.joursActifs = [...JOURS_ACTIFS_PAR_DEFAUT];
		this.snapshotHoraireAvantIdentique = null;
		this.snapshotJoursActifsAvantIdentique = null;
		this.journeeModeIdentique = { Id: 0 };
		this.champsEnErreur = [];
	}

	/**
	 * Ferme la modale sans sauvegarder.
	 */
	public annuler(): void {
		this.visible = false;
	}

	/**
	 * Indique si un champ est en erreur de validation.
	 * @param cleChamp - La clé du champ (ex. "0-matin-debut").
	 * @returns `true` si le champ est en erreur.
	 */
	public champEnErreur(cleChamp: string): boolean {
		return this.champsEnErreur.includes(cleChamp);
	}

	/**
	 * Formate une Date pour `input type="time"`.
	 * @param dateValue - La date à formater.
	 * @returns L'heure "HH:mm" ou `""`.
	 */
	public formatTime(dateValue: Date | null | undefined): string {
		return formatHeureAffichage(dateValue);
	}

	/**
	 * Formate une durée HH:mm pour l'affichage utilisateur (ex. "7h30").
	 * @param hhMm - La durée "HH:mm".
	 * @returns La durée formatée.
	 */
	public formatDureeAffichage(hhMm: string): string {
		return formatDureeHhMm(hhMm);
	}

	/**
	 * Émis à la fermeture de la modale (croix ou après sauvegarde).
	 */
	public onDialogClosed(): void {
		this.$emit("closeModal");
	}

	// ── Méthodes privées ────────────────────────────────────────────────────

	/**
	 * Collecte les erreurs de validation de toutes les journées actives.
	 * En mode identique, seule la journée modèle est validée.
	 * @returns Les messages et champs en erreur agrégés.
	 */
	private collecterErreursValidation(): ErreursValidationJournee {
		if (this.horairesIdentiques) {
			return validerJourneeHoraire(this.journeeModeIdentique, 0, "mode");
		}

		const messages: string[] = [];
		const champsErreur: string[] = [];

		this.journees.forEach((journee, index) => {
			if (!this.joursActifs[index]) {
				return;
			}
			const erreurs = validerJourneeHoraire(journee, index, String(index));
			messages.push(...erreurs.messages);
			champsErreur.push(...erreurs.champsErreur);
		});

		return { messages, champsErreur };
	}

	/**
	 * Applique la valeur d'un input time sur un champ de journée (via `$set` pour la réactivité Vue).
	 * @param journee - La journée cible.
	 * @param field - Le champ à modifier.
	 * @param event - L'événement natif.
	 */
	private appliquerSaisieHeure(
		journee: HoraireJourneeDto,
		field: keyof HoraireJourneeDto,
		event: Event
	): void {
		const value = (event.target as HTMLInputElement).value;
		const heure = parseHeureInput(value);
		this.$set(journee, field, heure ?? undefined);
	}

	/**
	 * Active le mode horaires identiques : sauvegarde le snapshot et copie la première journée renseignée.
	 */
	private activerModeIdentique(): void {
		if (this.horairesIdentiques) {
			return;
		}
		this.snapshotHoraireAvantIdentique = JSON.parse(JSON.stringify(this.horaireHebdo));
		this.snapshotJoursActifsAvantIdentique = [...this.joursActifs];
		this.horairesIdentiques = true;
		const source = premiereJourneeActiveAvecValeur(this.journees, this.joursActifs);
		this.journeeModeIdentique = source ? { ...source } : { Id: 0 };
		this.joursActifs = [true, true, true, true, true, false];
		this.synchroniserJoursActifsDepuisModeIdentique();
	}

	/**
	 * Restaure le mode personnalisé depuis le snapshot pris à l'activation.
	 */
	private desactiverModeIdentique(): void {
		if (!this.horairesIdentiques) {
			return;
		}
		this.horairesIdentiques = false;
		if (this.snapshotHoraireAvantIdentique) {
			this.horaireHebdo = JSON.parse(JSON.stringify(this.snapshotHoraireAvantIdentique));
			this.snapshotHoraireAvantIdentique = null;
		}
		if (this.snapshotJoursActifsAvantIdentique) {
			this.joursActifs = [...this.snapshotJoursActifsAvantIdentique];
			this.snapshotJoursActifsAvantIdentique = null;
		}
	}

	/**
	 * Propage la journée modèle (mode identique) vers tous les jours cochés.
	 */
	private synchroniserJoursActifsDepuisModeIdentique(): void {
		this.journees.forEach((journee, index) => {
			if (this.joursActifs[index]) {
				this.copierJourneeReactive(this.journeeModeIdentique, journee);
			}
		});
	}

	/**
	 * Copie les champs horaires d'une journée vers une autre (via `$set` pour la réactivité Vue).
	 * @param source - La journée source.
	 * @param cible - La journée cible.
	 */
	private copierJourneeReactive(source: HoraireJourneeDto, cible: HoraireJourneeDto): void {
		CHAMPS_HORAIRE.forEach(champ => {
			this.$set(cible, champ, source[champ]);
		});
	}
}



// WEBPACK FOOTER //
// ./src/components/modalComponents/modalHoraireHebdo/modalHoraireHebdo.ts